import React, { useEffect, useState, useRef, useMemo } from "react";
import './detail.css';
import { Image } from "antd";
import JsonRequest from "../components/jsonRequest";
import { useParams, useSearchParams } from "react-router-dom";
import logo from '../images/把脉logo.png';
import configFunc from "../config";
import PulseSection from './components/PulseSection';
import DetailTabs from './components/DetailTabs';
import WechatOpenAppButton from "./components/WechatOpenAppButton";
import { checkType } from "./const";
const pulseImagesContext = require.context('../pulse_images', false, /\.(png|jpe?g|gif)$/);

const imageMap: Record<string, string> = {};
pulseImagesContext.keys().forEach((item: string) => {
    const fullImageName = item.replace('./', '');
    const imageName = fullImageName.replace(/\.[^/.]+$/, '');
    imageMap[imageName] = pulseImagesContext(item);
});

const getImageSource = (imageName: string, defaultUrl?: string) => {
    if (imageName) {
        // 提取基础名称
        const baseName = imageName.replace(/[^\u4e00-\u9fa5]/g, '');
        // 首先尝试精确匹配
        if (imageMap[imageName]) {
            return imageMap[imageName];
        }
        // 然后尝试基础名称匹配
        if (imageMap[baseName]) {
            return imageMap[baseName];
        }
    }
    return defaultUrl || '';
};

declare global {
    interface Window {
        wx: any;
    }
}

const DetailPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'report' | 'plan'>('report');
    const [mediaDataMap, setMediaDataMap] = useState<Record<string, any>>({});
    const [data, setData] = useState<any>();
    const [showTopText, setShowTopText] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const { id, dev } = useParams<{ id: string; dev?: string }>();
    const [searchParams] = useSearchParams();
    const type = searchParams.get('type');
    let baseUrl = dev ? configFunc.DevHttpEndpoint("") : configFunc.HttpEndpoint("");

    const getDetail = async () => {
        if (type === 'PULSE' || !type) {
            JsonRequest.rawClient(baseUrl).get(`/common/pulse/detail/${id}`, {})
                .then((resp: any) => resp.json())
                .then(async ({ data }: any) => {
                    if (data?.pulseDetail?.mediaList) {
                        const map: Record<string, any> = {};
                        data.pulseDetail.mediaList.forEach((item: any) => {
                            map[item.fieldKey] = item.extData;
                        });
                        setMediaDataMap(map);
                    }
                    setData({ ...data, type: type });
                });
        } else if (type === 'FOOD_RECOGNITION') {
            JsonRequest.rawClient(baseUrl).get(`/common/food/recognition/info/${id}`, {})
                .then((resp: any) => resp.json())
                .then(async ({ data }: any) => {
                    setData({ ...data, type: type, foodsCount: data?.foodsData?.results?.length || 0 });
                });
        } else {
            JsonRequest.rawClient(baseUrl).get(`/common/check-detail/${id}`, {})
                .then((resp: any) => resp.json())
                .then(async ({ data }: any) => {
                    if (data?.pulseDetail?.mediaList) {
                        const map: Record<string, any> = {};
                        data.pulseDetail.mediaList.forEach((item: any) => {
                            map[item.fieldKey] = item.extData;
                        });
                        setMediaDataMap(map);
                    }
                    setData({ ...data, type: type });
                });
        }
    };

    useEffect(() => {
        if (id) {
            getDetail();
        }
    }, [id])

    // 滚动配置：根据类型和tab组合设置不同的遮挡比例和高度限制
    const scrollConfig = useMemo(() => ({
        // 脉象相关配置
        PULSE: {
            // 诊断报告
            report: {
                targetClass: '.primary-content',
                threshold: 0.5,
                offset: 0,
                height: '100%'
            },
            // 调理方案
            plan: {
                targetClass: '.plan-span-title-little',
                threshold: 0.5,
                offset: 0,
                height: '90%'
            }
        },
        // 舌诊相关配置
        TONGUE: {
            report: {
                targetClass: '.box-container',
                threshold: 0.5,
                offset: 0,
                height: '80%'
            },
            plan: {
                targetClass: '.box-container',
                threshold: 0.5,
                offset: 0,
                height: '90%'
            }
        },
        // 体质检测相关配置
        CORPOREALITY: {
            report: {
                targetClass: '.box-container',
                threshold: 0.5,
                offset: 0,
                height: '100%'
            },
            plan: {
                targetClass: '.box-container',
                threshold: 0.5,
                offset: 0,
                height: '75%'
            }
        },
    }), []);

    // 默认配置，用于兜底
    const defaultConfig = useMemo(() => ({
        targetClass: '.primary-content',
        threshold: 0.9,
        offset: 0,
        height: '88%' // 默认高度，占当前页面的百分比
    }), []);

    // 获取当前类型和tab对应的配置
    const currentConfig = useMemo(() => {
        const validType = (type && Object.keys(checkType).includes(type)) ? type : 'PULSE';
        return (validType && scrollConfig[validType as keyof typeof scrollConfig]?.[activeTab]) || defaultConfig;
    }, [scrollConfig, type, activeTab, defaultConfig]);

    // 修改滚动处理逻辑
    useEffect(() => {
        const contentElement = contentRef.current;
        if (!contentElement) return;
        // 基于内容的实际可见性来决定是否显示底部提示
        const handleScroll = () => {
            const currentScrollTop = contentElement.scrollTop;
            // 计算内容总高度与可视区域高度的差值
            const scrollableHeight = contentElement.scrollHeight - contentElement.clientHeight;
            // 当滚动到距离底部较近的位置时显示提示
            const isNearBottom = scrollableHeight > 0 && currentScrollTop >= scrollableHeight - 50;
            if (isNearBottom) {
                setShowTopText(true);
            } else {
                setShowTopText(false);
            }
        };
        // 添加滚动事件监听
        contentElement.addEventListener('scroll', handleScroll);
        // 初始检查一次
        handleScroll();
        // 清理函数
        return () => {
            contentElement.removeEventListener('scroll', handleScroll);
        };
    }, [activeTab, type]);

    return (
        <div className="detail-main">
            <div className="content-wrapper" ref={contentRef}>
                {/* 脉象信息部分 */}
                <PulseSection data={data} />

                {/* Tab内容区域 */}
                <div className="detail-tabs">
                    {(type !== 'FOOD_RECOGNITION' || data?.foodsCount > 4) && <div className="tabs">
                        <div
                            className={`tab ${activeTab === 'report' ? 'active' : ''}`}
                            onClick={() => setActiveTab('report')}
                        >
                            {type === 'FOOD_RECOGNITION' && data?.foodsCount > 4 ? "识菜详情" : "诊断报告"}
                        </div>
                        <div
                            className={`tab ${activeTab === 'plan' ? 'active' : ''}`}
                            onClick={() => setActiveTab('plan')}
                        >
                            {type === 'FOOD_RECOGNITION' && data?.foodsCount > 4 ? "养生建议" : "调理方案"}
                        </div>
                    </div>}

                    {/* Tab内容区域 - 应用配置的高度限制 */}
                    <div style={{
                        backgroundColor: '#FAF9F9',
                        minHeight: 'calc(80vh)',
                        ...(data?.foodsCount <= 4 ? {
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                        } : {})
                    }}>
                        <DetailTabs activeTab={activeTab} height={currentConfig.height} data={data} mediaDataMap={mediaDataMap} getImageSource={getImageSource} />
                    </div>
                </div>

                {/* 底部区域 - top-text根据滚动状态显示/隐藏 */}
                <div className="footer">
                    <div className="top-text" style={{ display: `${showTopText ? 'block' : 'none'}` }}>下载知源把脉养生查看更多内容</div>
                    <div className="app-section">
                        <div className="app-info">
                            <div className="app-icon">
                                <Image src={logo} className="icon-placeholder" preview={false} />
                            </div>
                            <div className="app-details">
                                <div className="app-name">知源把脉养生</div>
                                <div className="app-description">智慧 · 健康 · 先知</div>
                            </div>
                        </div>
                        <WechatOpenAppButton
                            style={{
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                borderRadius: '22px',
                                backgroundColor: '#94724B',
                            }}
                            buttonText="App内打开"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailPage;